import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { lazy, Suspense, useEffect } from 'react'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { OrgProvider } from './contexts/OrgContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { ErrorBoundary } from './components/ErrorBoundary'
import { CircuitBackground } from './components/CircuitBackground'
import Layout from './components/Layout'
import { PublicNavBar } from './components/PublicNavBar'
import ProtectedRoute from './components/ProtectedRoute'
import AcceptInvite from './pages/AcceptInvite'
import Login from './pages/Login'
import Register from './pages/Register'
import OAuthCallback from './pages/OAuthCallback'
import NotFound from './pages/NotFound'
import Home from './pages/Home'
import GettingStarted from './pages/GettingStarted'
import { LoadingSpinner } from './components/LoadingSpinner'
import VerifyEmail from './pages/VerifyEmail'
import CheckEmail from './pages/CheckEmail'
import EstimatePage from './pages/EstimatePage'
import EstimateExamples from './pages/EstimateExamples'
import ProductsPage from './pages/ProductsPage'
import Packages from './pages/Packages'
import PackageDetail from './pages/PackageDetail'
import Pricing from './pages/Pricing'
import Terms from './pages/Terms'
import Privacy from './pages/Privacy'
import HowWeUseAI from './pages/HowWeUseAI'
import Blog from './pages/Blog'
import BlogPost from './pages/BlogPost'

// Signed-in app and admin pages load on demand: every visitor used to download
// them all (one 571 KB gz chunk). Public pages stay eager — they are prerendered.
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Projects = lazy(() => import('./pages/Projects'))
const NewProject = lazy(() => import('./pages/NewProject'))
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'))
const Workflows = lazy(() => import('./pages/Workflows').then((m) => ({ default: m.Workflows })))
const Templates = lazy(() => import('./pages/Templates'))
const Organizations = lazy(() => import('./pages/Organizations'))
const OrgSettings = lazy(() => import('./pages/OrgSettings'))
const Teams = lazy(() => import('./pages/Teams'))
const Billing = lazy(() => import('./pages/Billing'))
const APIKeys = lazy(() => import('./pages/APIKeys'))
const AuditLogs = lazy(() => import('./pages/AuditLogs'))
const Profile = lazy(() => import('./pages/Profile'))
const Docs = lazy(() => import('./pages/Docs'))
const Admin = lazy(() => import('./pages/Admin'))
const AdminStorage = lazy(() => import('./pages/AdminStorage'))
const AdminAI = lazy(() => import('./pages/AdminAI'))
const AdminCache = lazy(() => import('./pages/AdminCache'))
const AdminImages = lazy(() => import('./pages/AdminImages'))
const AdminEstimates = lazy(() => import('./pages/AdminEstimates'))
const ImportProject = lazy(() => import('./pages/ImportProject'))
const AdminNotifications = lazy(() => import('./pages/AdminNotifications'))
const AdminPreview = lazy(() => import('./pages/AdminPreview'))
const AdminBilling = lazy(() => import('./pages/AdminBilling'))
const AdminSentry = lazy(() => import('./pages/AdminSentry'))
const AdminReview = lazy(() => import('./pages/AdminReview'))
const CodeReview = lazy(() => import('./pages/CodeReview'))
const UserNotifications = lazy(() => import('./pages/UserNotifications'))
const Agents = lazy(() => import('./pages/Agents'))
const BasicBuilder = lazy(() => import('./pages/BasicBuilder'))
const BasicProjectChat = lazy(() => import('./pages/BasicProjectChat'))
const AdminEmailVerification = lazy(() => import('./pages/AdminEmailVerification'))
const Feedback = lazy(() => import('./pages/Feedback'))
const AdminFeedback = lazy(() => import('./pages/AdminFeedback'))
const ShipView = lazy(() => import('./pages/ShipView'))
const ConnectRepoFlow = lazy(() => import('./pages/ConnectRepoFlow'))
const SavingsDashboard = lazy(() => import('./pages/SavingsDashboard'))
const CreatePackage = lazy(() => import('./pages/CreatePackage'))

// Strips trailing whitespace from pathnames — works around GitHub App callback URLs
// that have accidental trailing spaces (e.g. /oauth/callback%20%20).
function PathNormalizer() {
  const location = useLocation()
  const navigate = useNavigate()
  useEffect(() => {
    const trimmed = location.pathname.trimEnd()
    if (trimmed !== location.pathname) {
      navigate({ pathname: trimmed, search: location.search }, { replace: true })
    }
  }, [location, navigate])
  return null
}

function LandingRouter() {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return <div className="flex items-center justify-center min-h-screen"><LoadingSpinner size="lg" /></div>
  if (isAuthenticated) return <Navigate to="/dashboard" replace />
  return <Home />
}

function PackagesRouter() {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return <div className="flex items-center justify-center min-h-screen"><LoadingSpinner size="lg" /></div>
  if (isAuthenticated) return <Layout><Packages /></Layout>
  return <Packages />
}

// /docs is public (prospects and search engines read it); signed-in users get
// it inside the app layout as before.
export function DocsRouter() {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return <div className="flex items-center justify-center min-h-screen"><LoadingSpinner size="lg" /></div>
  if (isAuthenticated) return <Layout><Docs /></Layout>
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicNavBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Docs />
      </main>
    </div>
  )
}

function PackageDetailRouter() {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return <div className="flex items-center justify-center min-h-screen"><LoadingSpinner size="lg" /></div>
  if (isAuthenticated) return <Layout><PackageDetail /></Layout>
  return <PackageDetail />
}

function CreatePackageRouter() {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return <div className="flex items-center justify-center min-h-screen"><LoadingSpinner size="lg" /></div>
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <Layout><CreatePackage /></Layout>
}

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <OrgProvider>
        <ThemeProvider>
      <PathNormalizer />
      <CircuitBackground intensity="low" />
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><LoadingSpinner size="lg" /></div>}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/check-email" element={<CheckEmail />} />
        <Route path="/" element={<LandingRouter />} />
        <Route path="/getting-started" element={<GettingStarted />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/how-we-use-ai" element={<HowWeUseAI />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/estimate" element={<EstimatePage />} />
        <Route path="/estimate/examples" element={<EstimateExamples />} />
        <Route path="/estimate/:id" element={<EstimatePage />} />
        <Route path="/packages" element={<PackagesRouter />} />
        <Route path="/packages/new" element={<CreatePackageRouter />} />
        <Route path="/packages/:slug" element={<PackageDetailRouter />} />
        <Route path="/docs" element={<DocsRouter />} />

        {/* Protected routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <ErrorBoundary>
                <Routes>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/build" element={<BasicBuilder />} />
                  <Route path="/build/:id" element={<BasicProjectChat />} />
                  <Route path="/projects" element={<Projects />} />
                  <Route path="/projects/new" element={<NewProject />} />
                  <Route path="/projects/import" element={<ImportProject />} />
                  <Route path="/projects/:id" element={<ProjectDetail />} />
                  <Route path="/projects/:id/ship" element={<ShipView />} />
                  <Route path="/projects/:id/start" element={<ConnectRepoFlow />} />
                  <Route path="/dashboard/savings" element={<SavingsDashboard />} />
                  <Route path="/agents" element={<Agents />} />
                  <Route path="/reviews" element={<CodeReview />} />
                  <Route path="/workflows" element={<Workflows />} />
                  <Route path="/workflows/*" element={<Workflows />} />
                  <Route path="/templates" element={<Templates />} />
                  {/* Marketplace was consolidated into Templates (Registries tab). */}
                  <Route path="/marketplace" element={<Navigate to="/templates" replace />} />
                  <Route path="/organizations" element={<Organizations />} />
                  <Route path="/organizations/:id/settings" element={<OrgSettings />} />
                  <Route path="/organizations/:id/teams" element={<Teams />} />
                  <Route path="/join/:token" element={<AcceptInvite />} />
                  <Route path="/billing" element={<Billing />} />
                  <Route path="/api-keys" element={<APIKeys />} />
                  <Route path="/audit-logs" element={<AuditLogs />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/admin/storage" element={<AdminStorage />} />
                  <Route path="/admin/ai" element={<AdminAI />} />
                  <Route path="/admin/cache" element={<AdminCache />} />
                  <Route path="/admin/images" element={<AdminImages />} />
                  <Route path="/admin/estimates" element={<AdminEstimates />} />
                  <Route path="/admin/notifications" element={<AdminNotifications />} />
                  <Route path="/admin/preview" element={<AdminPreview />} />
                  <Route path="/admin/billing" element={<AdminBilling />} />
                  <Route path="/admin/sentry" element={<AdminSentry />} />
                  <Route path="/admin/review" element={<AdminReview />} />
                  <Route path="/admin/email-verification" element={<AdminEmailVerification />} />
                  <Route path="/admin/feedback" element={<AdminFeedback />} />
                  <Route path="/feedback" element={<Feedback />} />
                  <Route path="/settings/notifications" element={<UserNotifications />} />
                  <Route path="/profile" element={<Profile />} />
                  {/* Unknown in-app path — the web server 404s URLs outside the
                      route table, so this only catches bad deep links. */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </ErrorBoundary>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
      </Suspense>
        </ThemeProvider>
      </OrgProvider>
    </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
